import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { CornerDownLeft } from 'lucide-react';
import CoilChip from '../CoilChip';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import NumberInput from '../../ui/number-input';
import {
    FormField,
    FormItem,
    FormLabel,
    FormControl,
    FormMessage,
} from '../../ui/form';
import { ToggleGroup, ToggleGroupItem } from '../../ui/toggle-group';
import { parseCoils } from '../utils';
import { MachineFormValues } from '../types';

const fieldStrengthValues = [1.5, 3, 7];
const toggleItemClass =
    'h-9 flex-1 border border-control bg-background text-sm data-[state=on]:border-blue data-[state=on]:bg-blue/10 data-[state=on]:text-blue';

export default function TechnicalSpecsSection() {
    const { t } = useTranslation();
    const form = useFormContext<MachineFormValues>();
    const coilInputRef = useRef<HTMLInputElement>(null);
    const [coilInputValue, setCoilInputValue] = useState('');
    const [isCustomFieldStrength, setIsCustomFieldStrength] = useState(
        () => !fieldStrengthValues.includes(form.getValues('FieldStrength'))
    );

    const addCoil = (value: string) => {
        const trimmed = value.trim();
        if (!trimmed) return;
        const existing = parseCoils(form.getValues('TRCoils'));
        if (existing.some((c) => c.toLowerCase() === trimmed.toLowerCase()))
            return;
        form.setValue('TRCoils', [...existing, trimmed].join(', '));
    };

    const removeCoil = (index: number) => {
        const existing = parseCoils(form.getValues('TRCoils'));
        existing.splice(index, 1);
        form.setValue('TRCoils', existing.join(', '));
    };

    return (
        <div className="space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
                <FormField
                    control={form.control}
                    name="Model"
                    rules={{ required: 'Model is required' }}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className="text-xs" required>
                                {t(
                                    'roomOverview.mriMachineForm.form.model.label'
                                )}
                            </FormLabel>
                            <FormControl>
                                <Input {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="Manufacturer"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className="text-xs">
                                {t(
                                    'roomOverview.mriMachineForm.form.manufacturer.label'
                                )}
                            </FormLabel>
                            <FormControl>
                                <Input {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
                <FormField
                    control={form.control}
                    name="FieldStrength"
                    rules={{
                        validate: (value) =>
                            value > 0 || 'Field strength is required',
                    }}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className="text-xs">
                                {t(
                                    'roomOverview.mriMachineForm.form.fieldStrength.label'
                                )}
                            </FormLabel>
                            <FormControl>
                                <ToggleGroup
                                    type="single"
                                    value={
                                        isCustomFieldStrength
                                            ? 'other'
                                            : String(field.value)
                                    }
                                    onValueChange={(val) => {
                                        if (!val) return;
                                        const isOther = val === 'other';
                                        setIsCustomFieldStrength(isOther);
                                        field.onChange(
                                            isOther ? '' : Number(val)
                                        );
                                    }}
                                    className="justify-start gap-2"
                                >
                                    {fieldStrengthValues.map((fs) => (
                                        <ToggleGroupItem
                                            key={fs}
                                            value={String(fs)}
                                            className={toggleItemClass}
                                        >
                                            {fs} T
                                        </ToggleGroupItem>
                                    ))}
                                    <ToggleGroupItem
                                        value="other"
                                        className={toggleItemClass}
                                    >
                                        {t(
                                            'roomOverview.mriMachineForm.form.fieldStrength.other'
                                        )}
                                    </ToggleGroupItem>
                                </ToggleGroup>
                            </FormControl>
                            {isCustomFieldStrength && (
                                <div className="flex items-center gap-2">
                                    <NumberInput
                                        value={field.value}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        step="any"
                                        min={0}
                                        placeholder={t(
                                            'roomOverview.mriMachineForm.form.fieldStrength.otherPlaceholder'
                                        )}
                                    />
                                    <span className="text-sm font-medium">
                                        T
                                    </span>
                                </div>
                            )}
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="TRCoils"
                    rules={{ required: 'TR Coils is required' }}
                    render={({ field }) => {
                        const coils = parseCoils(field.value);
                        return (
                            <FormItem>
                                <FormLabel className="text-xs" required>
                                    {t(
                                        'roomOverview.mriMachineForm.form.trCoils.label'
                                    )}
                                </FormLabel>
                                <div className="flex gap-2">
                                    <FormControl>
                                        <Input
                                            ref={coilInputRef}
                                            placeholder="e.g. Head, Body, Knee"
                                            value={coilInputValue}
                                            onChange={(e) =>
                                                setCoilInputValue(
                                                    e.target.value
                                                )
                                            }
                                            onKeyDown={(e) => {
                                                if (
                                                    e.key === 'Enter' ||
                                                    e.key === ','
                                                ) {
                                                    e.preventDefault();
                                                    addCoil(coilInputValue);
                                                    setCoilInputValue('');
                                                }
                                            }}
                                        />
                                    </FormControl>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="icon"
                                        className="h-10 w-10 shrink-0"
                                        disabled={!coilInputValue.trim()}
                                        onClick={() => {
                                            addCoil(coilInputValue);
                                            setCoilInputValue('');
                                            coilInputRef.current?.focus();
                                        }}
                                    >
                                        <CornerDownLeft className="h-4 w-4" />
                                    </Button>
                                </div>
                                {coils.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 pt-1">
                                        {coils.map((coil, i) => (
                                            <CoilChip
                                                key={i}
                                                coil={coil}
                                                onRemove={() => removeCoil(i)}
                                            />
                                        ))}
                                    </div>
                                )}
                                <FormMessage />
                            </FormItem>
                        );
                    }}
                />
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
                <FormField
                    control={form.control}
                    name="SlewRatePerAxis"
                    rules={{ required: 'Max slew rate is required' }}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className="text-xs" required>
                                {t(
                                    'roomOverview.mriMachineForm.form.slewRate.perAxis'
                                )}
                            </FormLabel>
                            <FormControl>
                                <NumberInput {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="MaxSlewRate"
                    rules={{ required: 'Max effective slew rate is required' }}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className="text-xs" required>
                                {t(
                                    'roomOverview.mriMachineForm.form.slewRate.maxEffective'
                                )}
                            </FormLabel>
                            <FormControl>
                                <NumberInput {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>

            <FormField
                control={form.control}
                name="MaxB1rms"
                rules={{ required: 'Max B1+rms is required' }}
                render={({ field }) => (
                    <FormItem className="sm:max-w-[calc(50%-0.625rem)]">
                        <FormLabel className="text-xs" required>
                            {t(
                                'roomOverview.mriMachineForm.form.maxB1rms.label'
                            )}
                        </FormLabel>
                        <FormControl>
                            <Input {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />
        </div>
    );
}
